import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ListaPrecio } from 'app/models/listaprecio.models';
import { ListaprecioService } from 'app/services/listaprecio.service';
import Swal from 'sweetalert2';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ProductosDialogoComponent } from '../productos-dialogo/productos-dialogo.component';

@Component({
  selector: 'app-create-lista-precio',
  templateUrl: './create-lista-precio.component.html',
  styleUrls: ['./create-lista-precio.component.css', './tabla-mejorada.css']
})
export class CreateListaPrecioComponent implements OnInit {
  listaPrecio: ListaPrecio;
  form: UntypedFormGroup;
  listaPrecios = [];
  total = 0;

  // Variables para la tabla de productos seleccionados
  productosDataSource: MatTableDataSource<any> = new MatTableDataSource();
  // Se ha removido la columna 'type' para optimizar espacio
  displayedColumns: string[] = ['code', 'name', 'price', 'priceInList', 'diferencia', 'actions'];
  productosSeleccionados: any[] = [];
  
  constructor(
    private fb: UntypedFormBuilder,
    private listaPrecioService: ListaprecioService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.form = this.fb.group({
      numero: new UntypedFormControl(''),
      code: new UntypedFormControl(''),
      desde: new UntypedFormControl(''),
      hasta: new UntypedFormControl(''),
      observacion: new UntypedFormControl(''),
      perfil: new UntypedFormControl('venta'),
      nombre: new UntypedFormControl(''),
      porcentajeAjuste: new UntypedFormControl(0),
      tipoAjuste: new UntypedFormControl('aumento'),
      incluirIva: new UntypedFormControl(false),
      incluirImpuestosInternos: new UntypedFormControl(false)
    });
  }

  ngOnInit(): void {
    const sidebar = <HTMLElement>document.querySelector('.navbar-brand');
    if (sidebar !== undefined) {
      sidebar.innerHTML = 'Nueva lista de precios';
    }
    this.listaPrecio = new ListaPrecio();
    this.getNextNumber();
    this.actualizarTablaProductos();
  }

  getNextNumber() {
    this.listaPrecioService.getNextNumber().subscribe(
      (data) => {
        if (data && data.nextNumber) {
          this.form.controls.numero.setValue(data.nextNumber, {
            onlySelf: true
          });
          this.form.controls.code.setValue(data.nextNumber, {
            onlySelf: true
          });
          // Deshabilitamos el campo para que no se pueda modificar
          this.form.controls.numero.disable();
        }
      },
      err => console.log(err)
    );
  }

  abrirDialogoProductos() {
    const dialogRef = this.dialog.open(ProductosDialogoComponent, {
      width: '60%',
      data: { productosSeleccionados: this.productosSeleccionados }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.length > 0) {
        // Obtenemos el número de lista de forma segura
        const numeroLista = this.form.getRawValue().numero;
        
        // Para cada producto seleccionado, verificamos si ya existe en la lista
        result.forEach(producto => {
          const index = this.productosSeleccionados.findIndex(p => p.id === producto.id);
          // Si ya existe, no lo agregamos de nuevo
          if (index === -1) {
            producto.priceListId = numeroLista;
            // Inicializar el precio personalizado con el precio base
            producto.customPrice = producto.price || 0;
            this.productosSeleccionados.push(producto);
          }
        });
        this.actualizarTablaProductos();
      }
    });
  }

  actualizarTablaProductos() {
    this.productosDataSource.data = this.productosSeleccionados;
  }

  aplicarAjustePorcentaje() {
    const porcentaje = this.form.value.porcentajeAjuste || 0;
    const tipoAjuste = this.form.value.tipoAjuste || 'aumento';
    const incluirIva = this.form.value.incluirIva || false;
    const incluirImpuestosInternos = this.form.value.incluirImpuestosInternos || false;

    if (porcentaje === 0) {
      Swal.fire(
        'Atención',
        'Debes ingresar un porcentaje válido para aplicar el ajuste',
        'warning'
      );
      return;
    }

    if (porcentaje < 0 && tipoAjuste === 'aumento') {
      Swal.fire(
        'Error de validación',
        'El porcentaje no puede ser negativo cuando se aplica como aumento',
        'warning'
      );
      return;
    }

    if (this.productosSeleccionados.length === 0) {
      Swal.fire(
        'Sin productos',
        'No hay productos seleccionados para aplicar el ajuste',
        'info'
      );
      return;
    }

    let productosActualizados = 0;

    this.productosSeleccionados.forEach(producto => {
      const precioBase = producto.price || 0;
      let precioCalculado = 0;

      if (tipoAjuste === 'aumento') {
        precioCalculado = precioBase * (1 + porcentaje / 100);
      } else {
        precioCalculado = precioBase * (1 - Math.abs(porcentaje) / 100);
      }

      // Aplicar IVA si corresponde
      if (incluirIva) {
        precioCalculado = precioCalculado * 1.21; // 21% de IVA
      }

      // Aplicar Impuestos Internos si corresponde
      // La tasa de impuestos internos puede variar según el producto, usamos un ejemplo de 8%
      if (incluirImpuestosInternos && producto.impuestoInterno) {
        const tasaImpuestoInterno = producto.impuestoInterno || 0.08; // Valor por defecto de 8% si no está definido
        precioCalculado = precioCalculado * (1 + tasaImpuestoInterno);
      }

      // Asegurar que el precio no sea negativo
      if (precioCalculado < 0) {
        precioCalculado = 0;
      }

      producto.customPrice = Math.round(precioCalculado * 100) / 100; // Redondear a 2 decimales
      productosActualizados++;
    });

    this.actualizarTablaProductos();

    const tipoTexto = tipoAjuste === 'aumento' ? 'aumento' : 'descuento';
    
    // Construir texto de impuestos aplicados
    let impuestosTexto = '';
    if (incluirIva && incluirImpuestosInternos) {
      impuestosTexto = ' (incluyendo IVA 21% e Impuestos Internos)';
    } else if (incluirIva) {
      impuestosTexto = ' (incluyendo IVA 21%)';
    } else if (incluirImpuestosInternos) {
      impuestosTexto = ' (incluyendo Impuestos Internos)';
    }
    
    Swal.fire(
      'Ajuste aplicado',
      `Se ha aplicado un ${tipoTexto} del ${Math.abs(porcentaje)}% a ${productosActualizados} productos${impuestosTexto}`,
      'success'
    );
  }

  getDiferencia(producto: any): number {
    const precioOriginal = producto.price || 0;
    const precioLista = producto.customPrice || 0;
    return precioLista - precioOriginal;
  }

  getDiferenciaFormateada(producto: any): string {
    const diferencia = this.getDiferencia(producto);
    
    if (diferencia === 0) {
      return '$0,00';
    }
    
    const signo = diferencia > 0 ? '+' : '';
    // Formatear con separadores de miles y comas para decimales (formato argentino)
    const diferenciaFormateada = Math.abs(diferencia).toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    return `${signo}$${diferencia < 0 ? '-' : ''}${diferenciaFormateada}`;
  }

  onPrecioChange(producto: any) {
    // Este método se llama cuando el usuario cambia manualmente el precio
    // Podemos agregar validaciones adicionales aquí si es necesario
    if (producto.customPrice < 0) {
      producto.customPrice = 0;
    }
  }

  validarPorcentaje() {
    const porcentaje = this.form.value.porcentajeAjuste;
    const tipoAjuste = this.form.value.tipoAjuste;

    if (tipoAjuste === 'aumento' && porcentaje < 0) {
      this.form.patchValue({ porcentajeAjuste: 0 });
      Swal.fire(
        'Valor inválido',
        'El porcentaje no puede ser negativo cuando se aplica como aumento',
        'warning'
      );
    }
  }

  resetearPrecios() {
    if (this.productosSeleccionados.length === 0) {
      return;
    }

    Swal.fire({
      title: '¿Resetear precios?',
      text: '¿Deseas restaurar todos los precios a sus valores originales?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, resetear',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        let productosResetados = 0;
        
        this.productosSeleccionados.forEach(producto => {
          producto.customPrice = producto.price || 0;
          productosResetados++;
        });

        this.actualizarTablaProductos();

        Swal.fire(
          'Precios restaurados',
          `Se han restaurado los precios originales de ${productosResetados} productos`,
          'success'
        );
      }
    });
  }

  getProductosConAumento(): number {
    return this.productosSeleccionados.filter(producto => this.getDiferencia(producto) > 0).length;
  }

  getProductosConDescuento(): number {
    return this.productosSeleccionados.filter(producto => this.getDiferencia(producto) < 0).length;
  }

  getProductosSinCambio(): number {
    return this.productosSeleccionados.filter(producto => this.getDiferencia(producto) === 0).length;
  }
  
  editarPrecio(producto: any) {
    Swal.fire({
      title: 'Precio para lista',
      input: 'number',
      inputValue: String(producto.customPrice || producto.price || 0),
      inputAttributes: {
        autocapitalize: 'off'
      },
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      showLoaderOnConfirm: true,
      preConfirm: (precio) => {
        const indice = this.productosSeleccionados.findIndex(p => p.id === producto.id);
        if (indice >= 0) {
          this.productosSeleccionados[indice].customPrice = parseFloat(precio);
          this.actualizarTablaProductos();
        }
      }
    });
  }

  eliminarProducto(producto: any) {
    Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas eliminar "${producto.name}" de la lista?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        const index = this.productosSeleccionados.findIndex(p => p.id === producto.id);
        if (index >= 0) {
          this.productosSeleccionados.splice(index, 1);
          this.actualizarTablaProductos();
        }
      }
    });
  }

  guardar() {
    if (this.productosSeleccionados.length === 0) {
      Swal.fire(
        'Atención',
        'Debes seleccionar al menos un producto para la lista de precios',
        'warning'
      );
      return;
    }

    // Obtenemos el valor del número directamente del control
    this.listaPrecio.code = this.form.getRawValue().numero;
    this.listaPrecio.nombre = this.form.value.nombre;
    this.listaPrecio.observacion = this.form.value.observacion;
    this.listaPrecio.description = ''; // Establecemos una cadena vacía ya que eliminamos el campo
    this.listaPrecio.perfil = this.form.value.perfil;
    
    // Incluimos los valores de los impuestos
    this.listaPrecio.incluirIva = this.form.value.incluirIva;
    this.listaPrecio.incluirImpuestosInternos = this.form.value.incluirImpuestosInternos;
    
    // Asignar los productos seleccionados
    this.listaPrecio.articulos = this.productosSeleccionados as any;
    
    Swal.fire({
      title: 'Guardando...',
      html: 'Por favor espera mientras guardamos la lista de precios',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
        this.listaPrecioService.guardarListaPrecio(this.listaPrecio).subscribe(
          (data) => {
            console.log(data);
            Swal.fire(
              '¡Excelente!',
              'Ya has creado la lista de precio con productos asociados',
              'success'
            ).then(() => {
              this.router.navigate(['configurar/lista-precio']);
            });
          },
          err => {
            console.error(err);
            Swal.fire(
              'Error',
              'Ha ocurrido un error al guardar la lista de precios',
              'error'
            );
          }
        );
      }
    });
  }
}
